<template>
  <div>
    <div class="video-sender">
      <video
        :class="!isStreaming.webcam ? 'nostream' : 'hide-toggles'"
        class="video"
        playsinline
        muted
        autoplay
        ref="webcam"/>
      <div class="video-sender-toggles">
        <span
          @click="toggleMicrophone"
          class="mic-toggle"
          v-if="isStreaming.webcam"
          :class="{ off: !isEnabled.microphone }">
        </span>
        <span
          @click="toggleWebcam"
          class="webcam-toggle"
          :class="{ off: !isEnabled.webcam }">
        </span>
        <span
          v-if="desktop"
          @click="toggleDesktop"
          class="desktop-toggle"
          :class="{ off: !isEnabled.desktop }">
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'VideoSender',
  props: {
    autostart: Boolean,
    desktop: Boolean,
  },
  data () {
    return {
      isStreaming: {
        webcam: false,
        desktop: false
      },
      streams: {},
      isEnabled: {
        microphone: true,
        webcam: false,
        desktop: false,
      },
    }
  },
  methods: {
    getWebcam () {
      const constraints = {
        video: {
          width: { max: 640 },
          height: { max: 480 },
        },
        audio: true,
        echoCancellation: true,
        noiseSuppression: true
      }

      return navigator.mediaDevices.getUserMedia(constraints)
    },
    getDesktop () {
      const constraints = {
        video: {
          width: { max: 1920 },
          height: { max: 1080 },
          frameRate: { max: 10 },
        }
      }

      return navigator.mediaDevices.getDisplayMedia(constraints)
    },
    stopTracks (name) {
      const stream = this.streams[name]
      stream.getTracks().forEach(track => track.stop())
      this.p2pClient.removeStream(name)
    },
    stopAllStreams () {
      Object.keys(this.streams)
        .filter(name => this.isStreaming[name])
        .forEach(this.stopTracks)
    },
    async broadcastWebcam () {
      try {
        const stream = await this.getWebcam()
        stream.oninactive = () => this.handleStreamInactive('webcam')
        this.streams.webcam = stream
      } catch {
        console.warn('Failed to capture the webcam')
        return
      }

      const { webcam } = this.$refs
      webcam.srcObject = this.streams.webcam

      this.getMicrophone().enabled = this.isEnabled.microphone
      this.p2pClient.addStream('webcam', this.streams.webcam)
      this.isStreaming.webcam = true
    },
    async broadcastDesktop () {
      try {
        const stream = await this.getDesktop()
        stream.oninactive = () => this.handleStreamInactive('desktop')
        this.streams.desktop = stream
      } catch {
        console.warn('Failed to capture the desktop')
        return
      }

      this.p2pClient.addStream('desktop', this.streams.desktop)
      this.isStreaming.desktop = true
    },
    stopBroadcast () {
      this.stopAllStreams()
      this.p2pClient.stop()
    },
    stopWebcam () {
      this.stopTracks('webcam')
    },
    stopDesktop () {
      this.stopTracks('desktop')
    },
    toggleMicrophone () {
      this.isEnabled.microphone = !this.isEnabled.microphone

      if (this.isStreaming.webcam) {
        this.getMicrophone().enabled = this.isEnabled.microphone
      }

      this.p2pClient.sendBroadcastMessage(
        this.isEnabled.microphone ? 'unmuted' : 'muted'
      )
    },
    async toggleDesktop () {
      if (this.isStreaming.desktop) {
        this.stopDesktop()
      } else {
        await this.broadcastDesktop()
      }

      this.isEnabled.desktop = this.isStreaming.desktop
    },
    async toggleWebcam () {
      if (this.isStreaming.webcam) {
        this.stopWebcam()
        this.$refs.webcam.srcObject = null
      } else {
        await this.broadcastWebcam()
      }

      this.isEnabled.webcam = this.isStreaming.webcam
    },
    handleStreamInactive (name) {
      delete this.streams[name]
      this.isStreaming[name] = false
      this.isEnabled[name] = false
    },
    getMicrophone () {
      const tracks = this.streams.webcam?.getAudioTracks()
      return tracks.length ? tracks[0] : {}
    },
    ...mapActions(['makeP2PClient']),
  },
  mounted () {
    this.makeP2PClient().then(() => {
      this.p2pClient.on('connection', conn => {
        conn.on('connected', () => conn.sendMessage(this.isEnabled.microphone ? 'unmuted' : 'muted'))
      })

      if (this.autostart) {
        this.toggleWebcam()
      }
    })
  },
  beforeDestroy () {
    if (this.isStreaming) {
      this.stopBroadcast()
    }
  },
  computed: {
    ...mapState(['p2pClient']),
  }
}
</script>

<style lang="scss" scoped>
@import "~@/styles/base.scss";

$toggle-size: 21px;
$toggle-shadow: 0 0 8px -3px;

.video-sender {
  position: relative;
  min-height: 150px;
  background-color: $main-light;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: $button-radius;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 150, 136, 0.6);

  .video {
    width: 100%;

    &.nostream {
      display: none;
    }
  }

  + button {
    margin-top: 10px;
  }
}

.hide-toggles {
  + .video-sender-toggles {
    transition: opacity 0.3s ease;
    opacity: 0.25;

    &:hover {
      opacity: 1;
    }
  }

  &:hover {
    + .video-sender-toggles {
      opacity: 1;
    }
  }
}

.video-sender-toggles {
  display: flex;
  position: absolute;
  column-gap: 5px;
  bottom: 8px;
}

@mixin toggle($icon-path, $icon-off-path) {
  display: block;
  position: relative;

  width: $toggle-size;
  height: $toggle-size;
  padding: 5px;

  background-image: url($icon-path);
  background-repeat: no-repeat;
  background-size: $toggle-size $toggle-size;
  background-color: #fafafa;
  background-position: 50% 50%;
  border-radius: 50%;
  box-shadow: $toggle-shadow #c4c4c4;

  cursor: pointer;
  transition: transform, box-shadow 0.05s ease;

  &.off {
    background-image: url($icon-off-path);
  }

  &:hover {
    transform: scale(1.1);
    box-shadow: $toggle-shadow #444141;
  }
}

.mic-toggle {
  @include toggle("~@/assets/mic.svg", "~@/assets/mic-off.svg");
}

.desktop-toggle {
  @include toggle("~@/assets/desktop.svg", "~@/assets/desktop-off.svg");
}

.webcam-toggle {
  @include toggle("~@/assets/webcam.svg", "~@/assets/webcam-off.svg");
}
</style>
